import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowUpRight, ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { AuthShell } from "@/layouts/AuthLayout";
import Logo from "@/components/Logo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <div className="space-y-4">
        <Logo size="h-20" to="/" className="rounded-lg shadow-sm border border-[hsl(var(--zt-gold))]/20 p-1" />
        <p className="font-heading uppercase tracking-[0.3em] text-[10px] text-[hsl(var(--zt-teal))]/65 font-bold">
          Password Recovery
        </p>
      </div>

      <h1 className="font-display text-4xl lg:text-5xl text-[hsl(var(--zt-teal))] leading-tight tracking-tight font-bold">
        Reset your <em className="text-[hsl(var(--zt-gold))] not-italic gold-text-glow font-display">password</em>.
      </h1>

      {sent ? (
        <div className="space-y-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 space-y-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 grid place-items-center">
              <Mail size={18} className="text-emerald-600" />
            </div>
            <p className="font-heading font-semibold text-emerald-800">Check your inbox</p>
            <p className="text-sm text-emerald-700 font-body leading-relaxed">
              If an account with <strong>{email}</strong> exists, you'll receive a password reset link shortly. The link expires in 1 hour.
            </p>
          </div>
          <Link
            to="/login"
            className="flex items-center gap-2 text-sm text-[hsl(var(--zt-charcoal))]/60 hover:text-[hsl(var(--zt-teal))] font-heading uppercase tracking-wider transition-colors"
          >
            <ArrowLeft size={14} /> Back to Login
          </Link>
        </div>
      ) : (
        <>
          <p className="font-body text-[14.5px] text-[hsl(var(--zt-charcoal))]/80 leading-relaxed">
            Enter your account email and we'll send you a secure link to reset your password.
          </p>

          <form onSubmit={submit} className="space-y-6">
            <label className="block space-y-2">
              <span className="font-heading text-[12.5px] font-semibold text-[hsl(var(--zt-teal))]">
                Email Address
              </span>
              <input
                type="email"
                required
                autoFocus
                className="input-editorial font-body"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </label>

            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="w-full bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/92 disabled:opacity-50 rounded-xl h-12 font-heading text-sm font-semibold tracking-tight flex items-center justify-center gap-2 elev-2 transition-all duration-200 hover:-translate-y-0.5 disabled:hover:translate-y-0"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : <ArrowUpRight size={16} />}
              {busy ? "Sending…" : "Send Reset Link"}
            </button>
          </form>

          <p className="font-body text-[13.5px] text-[hsl(var(--zt-charcoal))]/75 text-center">
            Remember your password?{" "}
            <Link to="/login" className="font-heading font-semibold text-[hsl(var(--zt-gold))] underline-offset-4 hover:underline">
              Log in
            </Link>
          </p>
        </>
      )}
    </AuthShell>
  );
}
